import { useEffect, useState } from "react";
import { Dice5, TrendingUp, ShieldCheck, Flame, ChevronRight, Info } from "lucide-react";
import { api } from "../lib/api";

const DATASETS = [
  { key: "OVERALL", label: "Overall" },
  { key: "PREGAME", label: "Pregame" },
  { key: "LIVE", label: "Live" },
];

const MATURITY_META = {
  NEW:          { color: "text-slate-400 border-slate-500/40 bg-slate-500/10",   text: "New · preview rating only" },
  PROVISIONAL:  { color: "text-amber-300 border-amber-500/40 bg-amber-500/10",   text: "Provisional (clamped 1300–1700)" },
  RANKED:       { color: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", text: "Ranked (clamped 1200–1900)" },
  ESTABLISHED:  { color: "text-[#E5B842] border-[#E5B842]/40 bg-[#E5B842]/10",   text: "Established — no cap" },
};

const TIER_PRESTIGE = {
  T12: { label: "BRONZE", class: "text-amber-700 border-amber-700/40 bg-amber-900/20" },
  T13: { label: "SILVER", class: "text-slate-200 border-slate-300/40 bg-slate-500/15" },
  T14: { label: "GOLD",   class: "text-[#FFD700] border-[#FFD700]/40 bg-[#FFD700]/10" },
};

function Stat({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg bg-[#0b0b0b] border border-[rgba(212,175,55,0.14)] p-3">
      <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-1">{label}</div>
      <div className="font-heading text-base sm:text-lg font-black text-slate-100 tabular-nums leading-none">{value}</div>
      {sub && <div className="text-[9px] font-mono text-slate-600 mt-1">{sub}</div>}
    </div>
  );
}

function MaturityBadge({ maturity, testid }) {
  const m = MATURITY_META[maturity] || MATURITY_META.NEW;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-heading font-bold uppercase tracking-wider border ${m.color}`}>
      <ShieldCheck className="w-3 h-3" />{maturity}
    </span>
  );
}

function TierBadge({ tier, label, testid }) {
  if (!tier) return null;
  const p = TIER_PRESTIGE[tier];
  if (p) return <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-heading font-bold uppercase tracking-wider border ${p.class}`}><Flame className="w-3 h-3" />{p.label}</span>;
  return <span data-testid={testid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-mono font-bold uppercase tracking-wider border border-slate-500/40 text-slate-400 bg-slate-500/10">{label || tier}</span>;
}

function DifficultyProfile({ profile, testid }) {
  if (!profile || profile.length === 0) {
    return <div data-testid={`${testid}-empty`} className="rounded-lg border border-dashed border-[rgba(212,175,55,0.2)] p-5 text-center text-[11px] font-mono text-slate-500 uppercase tracking-widest">No settled coupons yet</div>;
  }
  return (
    <div data-testid={testid} className="rounded-lg bg-[#0b0b0b] border border-[rgba(212,175,55,0.14)] overflow-hidden">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[9px] uppercase tracking-wider text-slate-500 font-mono bg-[#070707]">
            <th className="text-left px-3 py-2">Tier</th>
            <th className="text-right px-3 py-2">Played</th>
            <th className="text-right px-3 py-2">W / L / V</th>
            <th className="text-right px-3 py-2">Success%</th>
            <th className="text-right px-3 py-2">Points</th>
          </tr>
        </thead>
        <tbody>
          {profile.map((t) => (
            <tr key={t.tier} data-testid={`${testid}-row-${t.tier}`} className="border-t border-[rgba(212,175,55,0.08)]">
              <td className="px-3 py-2"><TierBadge tier={t.tier} label={t.label} testid={`tier-badge-${t.tier}`} /></td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-300">{t.played}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-400">
                <span className="text-emerald-400">{t.won}</span>/<span className="text-rose-400">{t.lost}</span>/<span className="text-slate-500">{t.void}</span>
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-300">{t.success_rate != null ? `${t.success_rate}%` : "—"}</td>
              <td className={`px-3 py-2 text-right font-heading font-black tabular-nums ${t.points > 0 ? "text-[#E5B842]" : t.points < 0 ? "text-rose-400" : "text-slate-500"}`}>{t.points > 0 ? `+${t.points.toFixed(1)}` : t.points.toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DatasetBlock({ ds, dataset }) {
  if (!dataset) return null;
  const hasRating = dataset.final_rating != null;
  const previewOnly = dataset.maturity === "NEW";
  const ratingText = hasRating ? dataset.display_rating : (dataset.has_activity ? "Preview" : "No activity");
  return (
    <div data-testid={`sportsbook-dataset-${ds}`}>
      <div className="flex items-center flex-wrap gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-mono">Sportsbook Rating · {ds}</div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="font-heading text-4xl font-black text-[#E5B842] tabular-nums leading-none" data-testid={`sportsbook-rating-${ds}`}>{ratingText}</span>
            {previewOnly && dataset.has_activity && <span className="text-[10px] font-mono uppercase tracking-wider text-amber-300">preview</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <MaturityBadge maturity={dataset.maturity} testid={`sportsbook-maturity-${ds}`} />
          {dataset.world_rank != null && <span data-testid={`sportsbook-world-rank-${ds}`} className="text-[11px] font-mono text-slate-300">World #{dataset.world_rank}</span>}
          {dataset.country_rank != null && <span data-testid={`sportsbook-country-rank-${ds}`} className="text-[11px] font-mono text-slate-300">Country #{dataset.country_rank}</span>}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
        <Stat label="Played" value={dataset.played} testid={`sportsbook-played-${ds}`} />
        <Stat label="Settled" value={dataset.settled} sub={`${dataset.void} void`} testid={`sportsbook-settled-${ds}`} />
        <Stat label="Won / Lost" value={<span><span className="text-emerald-400">{dataset.won}</span><span className="text-slate-500">/</span><span className="text-rose-400">{dataset.lost}</span></span>} testid={`sportsbook-wl-${ds}`} />
        <Stat label="Success" value={dataset.success_rate != null ? `${dataset.success_rate}%` : "N/A"} testid={`sportsbook-success-${ds}`} />
        <Stat label="Points" value={<span className={dataset.points > 0 ? "text-[#E5B842]" : dataset.points < 0 ? "text-rose-400" : "text-slate-200"}>{dataset.points > 0 ? `+${dataset.points.toFixed(1)}` : dataset.points.toFixed(1)}</span>} testid={`sportsbook-points-${ds}`} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 mt-2.5">
        <Stat label="Perf / 100" value={dataset.performance_100 != null ? dataset.performance_100.toFixed(2) : "N/A"} testid={`sportsbook-perf-${ds}`} />
        <Stat label="Avg Odds" value={dataset.avg_odds != null ? dataset.avg_odds.toFixed(2) : "—"} testid={`sportsbook-avgodds-${ds}`} />
        <Stat label="Avg Winning" value={dataset.avg_winning_odds != null ? dataset.avg_winning_odds.toFixed(2) : "—"} testid={`sportsbook-avgwin-${ds}`} />
        <Stat
          label="Highest Winning"
          value={dataset.highest_winning_odds != null ? dataset.highest_winning_odds.toFixed(2) : "—"}
          sub={dataset.highest_winning_partner ? `via ${dataset.highest_winning_partner.name}` : null}
          testid={`sportsbook-highest-${ds}`}
        />
        <Stat label="Confidence" value={`${(dataset.confidence * 100).toFixed(1)}%`} testid={`sportsbook-confidence-${ds}`} />
      </div>
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-mono mb-2">Difficulty Profile</div>
        <DifficultyProfile profile={dataset.difficulty_profile} testid={`sportsbook-difficulty-${ds}`} />
      </div>
    </div>
  );
}

export function SportsbookPassport({ wsbfId }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("OVERALL");
  useEffect(() => {
    let alive = true;
    api.get(`/players/${wsbfId}/sportsbook`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ datasets: {} }); });
    return () => { alive = false; };
  }, [wsbfId]);
  if (!data) return <div className="rounded-xl bg-[#141414] border border-[rgba(212,175,55,0.18)] p-5 animate-pulse h-56" data-testid="sportsbook-passport-loading" />;
  const current = data.datasets?.[tab];
  const hasAnything = Object.values(data.datasets || {}).some((d) => d && d.played > 0);
  if (!hasAnything) {
    return (
      <div data-testid="sportsbook-passport-empty" className="rounded-xl bg-[#141414] border border-[rgba(212,175,55,0.18)] p-5">
        <div className="flex items-center gap-2 mb-2">
          <Dice5 className="w-4 h-4 text-[#E5B842]" />
          <span className="font-heading text-sm font-black uppercase tracking-tight text-slate-100">Sportsbook</span>
        </div>
        <div className="text-[11px] font-mono text-slate-500 uppercase tracking-widest py-6 text-center">
          No sportsbook activity yet · connect a partner to start building a rating
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-[#141414] border border-[rgba(212,175,55,0.18)] p-5" data-testid="sportsbook-passport">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Dice5 className="w-4 h-4 text-[#E5B842]" />
          <span className="font-heading text-sm font-black uppercase tracking-tight text-slate-100">Sportsbook</span>
          <span className="text-[10px] font-mono text-slate-500">· {data.calculation_version}</span>
        </div>
        <a href="/sportsbook/methodology" className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-[#E5B842]" data-testid="sportsbook-methodology-link" style={{ transition: "color 0.15s ease" }}>
          <Info className="w-3 h-3" /> Methodology <ChevronRight className="w-3 h-3" />
        </a>
      </div>
      <div className="flex gap-1 mb-5 bg-[#0a0a0a] border border-[#2b2512] rounded-lg p-1 w-fit" data-testid="sportsbook-tabs">
        {DATASETS.map((d) => (
          <button key={d.key} onClick={() => setTab(d.key)} data-testid={`sportsbook-tab-${d.key}`}
            className={`px-3 py-1.5 rounded-md text-[11px] font-heading font-bold uppercase tracking-wide ${tab === d.key ? "bg-[#E5B842] text-black" : "text-slate-300 hover:text-[#E5B842]"}`}
            style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>
            {d.label}
          </button>
        ))}
      </div>
      <DatasetBlock ds={tab} dataset={current} />
    </div>
  );
}
